import type { Property } from '@vibesites/schema';

type PropertyAddressUrlFields = Pick<
  Property,
  | 'property_google_place_url'
  | 'property_google_business_url'
  | 'property_full_address'
  | 'property_address_latitude'
  | 'property_address_longitude'
>;

const GOOGLE_MAPS_SEARCH_URL = 'https://www.google.com/maps/search/';

function hasValue(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.trim().toLowerCase() !== 'false';
}

export function extractGooglePlaceId(url: string | null | undefined): string | null {
  if (!hasValue(url)) return null;

  const match = url.trim().match(/[?&]q=place_id:([^&]+)/) || url.trim().match(/place_id[:=]([^&]+)/);
  if (!match?.[1]) return null;

  return decodeURIComponent(match[1]);
}

export function isGoogleMapsPlaceUrl(url: string | null | undefined): boolean {
  if (!hasValue(url)) return false;

  try {
    const parsed = new URL(url.trim());
    const hostname = parsed.hostname.toLowerCase();
    const isHttpUrl = parsed.protocol === 'https:' || parsed.protocol === 'http:';
    return isHttpUrl && hostname.endsWith('google.com') && parsed.pathname.includes('/maps/place/');
  } catch {
    return false;
  }
}

export function buildGoogleMapsSearchUrl(query: string, placeId?: string | null): string {
  const params = new URLSearchParams({ api: '1', query });
  if (hasValue(placeId)) params.set('query_place_id', placeId);
  return `${GOOGLE_MAPS_SEARCH_URL}?${params.toString()}`;
}

export function getPropertyAddressUrl(property: Partial<PropertyAddressUrlFields> | null | undefined): string | null {
  if (!property) return null;

  const placeId = extractGooglePlaceId(property.property_google_place_url);
  if (placeId) return buildGoogleMapsSearchUrl('apartment', placeId);

  if (isGoogleMapsPlaceUrl(property.property_google_business_url)) {
    return property.property_google_business_url?.trim() ?? null;
  }

  if (hasValue(property.property_full_address)) {
    return buildGoogleMapsSearchUrl(property.property_full_address);
  }

  const lat = property.property_address_latitude;
  const lon = property.property_address_longitude;
  if (hasValue(lat) && hasValue(lon)) return buildGoogleMapsSearchUrl(`${lat},${lon}`);

  return null;
}
